<template>
  <a-modal
    class="demo-register-modal"
    :visible="visible"
    :footer="null"
    :maskClosable="false"
    :width="460"
    @cancel="closeModal"
  >
    <div class="modal-head">
      <p class="modal-kicker">{{ text.kicker }}</p>
      <h3 class="modal-title">{{ text.title }}</h3>
      <p class="modal-desc">{{ text.desc }}</p>
    </div>

    <div class="form-block">
      <label class="field-label">{{ text.phoneLabel }}</label>
      <a-input
        v-model.trim="form.phone"
        class="field-input"
        :placeholder="text.phonePlaceholder"
        :maxLength="11"
      />
    </div>

    <div class="form-block">
      <label class="field-label">{{ text.codeLabel }}</label>
      <div class="code-row">
        <a-input
          v-model.trim="form.code"
          class="field-input"
          :placeholder="text.codePlaceholder"
          :maxLength="6"
        />
        <a-button
          class="code-btn"
          :disabled="!phoneValid || countdown > 0"
          @click="handleGetCode"
        >
          {{ codeButtonText }}
        </a-button>
      </div>
    </div>

    <a-checkbox v-model="agreed" class="agreement">
      <span>
        {{ text.agreementPrefix }}
        <a href="javascript:void(0)" @click.prevent>{{ text.userAgreement }}</a>
        {{ text.and }}
        <a href="javascript:void(0)" @click.prevent>{{ text.privacyPolicy }}</a>
      </span>
    </a-checkbox>

    <a-button
      block
      type="primary"
      class="submit-btn"
      :disabled="!canSubmit"
      :loading="submitting"
      @click="handleSubmit"
    >
      {{ text.submitText }}
    </a-button>
  </a-modal>
</template>

<script>
const I18N_TEXT = {
  zh: {
    kicker: "ESHOP ERP",
    title: "免费注册，预约产品演示",
    desc: "填写手机号并验证后，顾问将尽快与您联系。",
    phoneLabel: "手机号",
    phonePlaceholder: "请输入 11 位手机号",
    codeLabel: "验证码",
    codePlaceholder: "请输入短信验证码",
    getCode: "获取验证码",
    retryCode: "s 后重试",
    agreementPrefix: "阅读并同意",
    userAgreement: "《用户协议》",
    and: "和",
    privacyPolicy: "《隐私政策》",
    submitText: "注册",
    phoneInvalidTip: "请输入正确的手机号",
    codeSentTip: "验证码已发送，请注意查收",
    submitSuccessTip: "注册成功，我们会尽快与您联系"
  },
  en: {
    kicker: "ESHOP ERP",
    title: "Free registration for a demo",
    desc: "Enter your phone and verify to get contacted by our consultant.",
    phoneLabel: "Phone",
    phonePlaceholder: "Enter 11-digit phone number",
    codeLabel: "Verification code",
    codePlaceholder: "Enter SMS code",
    getCode: "Get code",
    retryCode: "s",
    agreementPrefix: "I have read and agree to",
    userAgreement: "User Agreement",
    and: "and",
    privacyPolicy: "Privacy Policy",
    submitText: "Register",
    phoneInvalidTip: "Please enter a valid phone number",
    codeSentTip: "Verification code sent",
    submitSuccessTip: "Registered successfully, we will contact you soon"
  },
  th: {
    kicker: "ESHOP ERP",
    title: "ลงทะเบียนฟรีเพื่อนัดเดโม",
    desc: "กรอกหมายเลขโทรศัพท์และยืนยันเพื่อให้ที่ปรึกษาติดต่อกลับ",
    phoneLabel: "เบอร์โทรศัพท์",
    phonePlaceholder: "กรอกเบอร์โทร 11 หลัก",
    codeLabel: "รหัสยืนยัน",
    codePlaceholder: "กรอกรหัส SMS",
    getCode: "รับรหัส",
    retryCode: "วิ",
    agreementPrefix: "ฉันได้อ่านและยอมรับ",
    userAgreement: "ข้อตกลงผู้ใช้",
    and: "และ",
    privacyPolicy: "นโยบายความเป็นส่วนตัว",
    submitText: "ลงทะเบียน",
    phoneInvalidTip: "กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง",
    codeSentTip: "ส่งรหัสยืนยันแล้ว",
    submitSuccessTip: "ลงทะเบียนสำเร็จ เราจะติดต่อกลับโดยเร็ว"
  }
};

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        phone: "",
        code: ""
      },
      agreed: false,
      countdown: 0,
      countdownTimer: null,
      submitting: false
    };
  },
  computed: {
    currentLocale() {
      const locale = (this.$i18n && this.$i18n.locale) || "zh";
      return I18N_TEXT[locale] ? locale : "zh";
    },
    text() {
      return I18N_TEXT[this.currentLocale];
    },
    phoneValid() {
      return /^1\d{10}$/.test(this.form.phone);
    },
    codeValid() {
      return /^\d{4,6}$/.test(this.form.code);
    },
    canSubmit() {
      return this.phoneValid && this.codeValid && this.agreed;
    },
    codeButtonText() {
      if (this.countdown > 0) {
        return `${this.countdown}${this.text.retryCode}`;
      }
      return this.text.getCode;
    }
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    closeModal() {
      this.$emit("update:visible", false);
      this.resetForm();
    },
    resetForm() {
      this.form.phone = "";
      this.form.code = "";
      this.agreed = false;
      this.submitting = false;
      this.stopCountdown();
      this.countdown = 0;
    },
    handleGetCode() {
      if (!this.phoneValid) {
        this.$message.warning(this.text.phoneInvalidTip);
        return;
      }
      this.$message.success(this.text.codeSentTip);
      this.startCountdown();
    },
    startCountdown() {
      this.stopCountdown();
      this.countdown = 60;
      this.countdownTimer = setInterval(() => {
        if (this.countdown <= 1) {
          this.stopCountdown();
          this.countdown = 0;
          return;
        }
        this.countdown -= 1;
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    handleSubmit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      setTimeout(() => {
        this.$message.success(this.text.submitSuccessTip);
        this.submitting = false;
        this.closeModal();
      }, 500);
    }
  }
};
</script>

<style scoped>
.modal-head {
  margin-bottom: 20px;
}

.modal-kicker {
  margin: 0;
  color: #2f6bff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.modal-title {
  margin: 8px 0 0;
  font-size: 24px;
  line-height: 1.3;
  color: #0a275d;
}

.modal-desc {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #5f6f8e;
}

.form-block + .form-block {
  margin-top: 14px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #31466d;
  font-size: 13px;
  font-weight: 600;
}

.code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 124px;
  gap: 10px;
}

.field-input /deep/ .ant-input {
  border-radius: 10px;
  height: 40px;
  border-color: #d2defa;
}

.field-input /deep/ .ant-input:focus {
  border-color: #5a90ff;
  box-shadow: 0 0 0 2px rgba(90, 144, 255, 0.12);
}

.code-btn {
  height: 40px;
  border-radius: 10px;
  border: 1px solid #bfd1ff;
  color: #2f6bff;
  font-weight: 600;
}

.code-btn:hover,
.code-btn:focus {
  border-color: #8cb0ff;
  color: #255be0;
}

.agreement {
  margin-top: 16px;
  color: #596b8b;
  line-height: 1.6;
}

.agreement a {
  color: #2f6bff;
}

.submit-btn {
  margin-top: 16px;
  height: 42px;
  border-radius: 10px;
  border-color: rgba(138, 198, 255, 0.28);
  background: linear-gradient(135deg, rgba(52, 149, 255, 0.96), rgba(44, 111, 255, 0.96));
  font-weight: 600;
}

.submit-btn:hover,
.submit-btn:focus {
  border-color: rgba(187, 223, 255, 0.5);
  background: linear-gradient(135deg, rgba(74, 169, 255, 0.98), rgba(64, 128, 255, 0.98));
}
</style>
